import React, { useRef } from 'react';
import classNames from 'classnames';
import { FormCellTW } from '../formCellTW/FormCellTW';
import { useAutoFocus } from '../hooks/useAutoFocus';
import { IconWrapper, IconWrapperProps } from './IconWrapper';
import { mergeTests } from '../../ui-toolkit/test-utilities/mergeTests';
import { IHasTestData } from '../../ui-toolkit/test-utilities/IHasTestData';

export interface TextInputTWProps extends IconWrapperProps, IHasTestData {
  /** The value attribute of input. */
  value?: string;
  /** Text that will appear in an empty input. */
  placeholder?: string;
  /** Additional Tailwind utility classname */
  className?: string;
  /** Message to display when the input is in an error state. When this is present, also visually highlights the component as in error. */
  errorText?: string;
  /** Hint text content. */
  hintText?: string;
  /** Highlights the input as a required field (does also perform additional validation). */
  isRequired?: boolean;
  /** This label appears above the input. */
  labelText?: string;
  /** Specifies that the text input should automatically get focus. */
  autoFocus?: boolean;
  /** all elements are disabled*/
  disabled?: boolean;
  /** A callback triggered when component is focused. */
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  /** A callback triggered when focus is removed. */
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  /** A callback fired when the value prop changes. */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  /** A callback triggered when the user press a key down. */
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  /** A name for the component for Formik*/
  name?: string;
  /** The component is read only and not editable */
  readOnly?: boolean;
  /** Type, can be number, phone, text, etc */
  type?: string;
}

export const TextInputTW: React.FC<TextInputTWProps> = ({
  value,
  className,
  disabled,
  labelText,
  errorText,
  isRequired,
  hintText,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  autoFocus,
  name,
  placeholder,
  LeftIcon,
  leftClassName,
  RightIcon,
  rightClassName,
  readOnly,
  type,
  ...rest
}) => {
  const textInputRef = useRef<HTMLInputElement>(null);

  useAutoFocus(!!autoFocus, textInputRef);

  return (
    <FormCellTW
      disabled={disabled}
      labelText={labelText}
      errorText={errorText}
      isRequired={isRequired}
      hintText={hintText}
      name={name}
      className={className}
    >
      <IconWrapper
        LeftIcon={LeftIcon}
        RightIcon={RightIcon}
        leftClassName={leftClassName}
        rightClassName={rightClassName}
      >
        <input
          type={type || 'text'}
          disabled={disabled}
          id={name}
          name={name}
          className={classNames(
            'xs:text-xs xs:leading-6 block h-10 w-full rounded-md border-0 py-1.5 ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-inset',
            !!errorText
              ? 'border-red-600 ring-red-600 focus:ring-red-600'
              : 'text-gray-900 placeholder:text-text-content-placeholder focus:ring-foundation-primary',
            LeftIcon ? 'pl-10' : 'pl-1.5',
            RightIcon ? 'pr-10' : 'pr-1.5'
          )}
          value={value}
          placeholder={placeholder}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          readOnly={readOnly}
          ref={textInputRef}
          data-test-id={mergeTests(rest['data-test-id'], `${name}-input-text-tailwind`)}
        />
      </IconWrapper>
    </FormCellTW>
  );
};
